import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ImageField } from "./MediaUpload";
import { L } from "./ProjectForm";

const useSection = (key) => {
  const { settings, reload } = useSite();
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings && value === null) setValue(settings[key] ?? null); }, [settings, key, value]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { [key]: value });
      await reload();
      toast.success("Saved — your website is updated");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  return { value, setValue, save, saving };
};

const Panel = ({ title, hint, children, onSave, saving, testid }) => (
  <div className="border border-line bg-ivory" data-testid={testid}>
    <div className="border-b border-line p-5">
      <h2 className="font-serif text-2xl text-wine">{title}</h2>
      {hint && <p className="text-xs text-muted-ink">{hint}</p>}
    </div>
    <div className="space-y-8 p-5">{children}</div>
    <div className="flex justify-end border-t border-line p-5">
      <button onClick={onSave} disabled={saving} className="btn btn-primary disabled:opacity-60" data-testid={`${testid}-save`}>{saving ? "Saving…" : "Save changes"}</button>
    </div>
  </div>
);

const T = ({ label, value, onChange, rows, testid, placeholder }) => (
  <div>
    <L>{label}</L>
    {rows ? <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} />
      : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} />}
  </div>
);

const updateAt = (list, i, k, v) => list.map((x, j) => (j === i ? { ...x, [k]: v } : x));

export const ServicesTab = () => {
  const s = useSection("services");
  const p = useSection("packages");
  if (!s.value || !p.value) return null;
  return (
    <div className="space-y-8">
      <Panel title="Services" hint="Shown on Home and the Services page." onSave={s.save} saving={s.saving} testid="admin-services">
        {s.value.map((svc, i) => (
          <div key={i} className="grid gap-4 border-b border-line pb-8 last:border-0 last:pb-0 sm:grid-cols-2">
            <T label="Service name (Services page)" value={svc.title} onChange={(v) => s.setValue(updateAt(s.value, i, "title", v))} testid={`svc-${i}-title`} />
            <T label="Short name (Home page)" value={svc.home_title} onChange={(v) => s.setValue(updateAt(s.value, i, "home_title", v))} testid={`svc-${i}-home-title`} />
            <T label="Home description" rows={2} value={svc.home_description} onChange={(v) => s.setValue(updateAt(s.value, i, "home_description", v))} testid={`svc-${i}-home-desc`} />
            <T label="Services page description" rows={2} value={svc.summary} onChange={(v) => s.setValue(updateAt(s.value, i, "summary", v))} testid={`svc-${i}-summary`} />
            <T label="What's included (one per line)" rows={5} value={(svc.includes || []).join("\n")} onChange={(v) => s.setValue(updateAt(s.value, i, "includes", v.split("\n")))} testid={`svc-${i}-includes`} />
            <ImageField label="Image" value={svc.image} onChange={(v) => s.setValue(updateAt(s.value, i, "image", v))} testid={`svc-${i}-image`} />
          </div>
        ))}
      </Panel>
      <Panel title="Packages" hint="No prices are shown unless you write them in the price label." onSave={p.save} saving={p.saving} testid="admin-packages">
        {p.value.map((pk, i) => (
          <div key={i} className="grid gap-4 border-b border-line pb-8 last:border-0 last:pb-0 sm:grid-cols-2">
            <T label="Package name" value={pk.name} onChange={(v) => p.setValue(updateAt(p.value, i, "name", v))} testid={`pkg-${i}-name`} />
            <T label="Price label" value={pk.price_label} onChange={(v) => p.setValue(updateAt(p.value, i, "price_label", v))} testid={`pkg-${i}-price`} />
            <T label="Best for" rows={2} value={pk.best_for} onChange={(v) => p.setValue(updateAt(p.value, i, "best_for", v))} testid={`pkg-${i}-best-for`} />
            <T label="Description" rows={2} value={pk.description} onChange={(v) => p.setValue(updateAt(p.value, i, "description", v))} testid={`pkg-${i}-desc`} />
            <T label="Button text" value={pk.cta_label} onChange={(v) => p.setValue(updateAt(p.value, i, "cta_label", v))} testid={`pkg-${i}-cta`} />
            <label className="flex items-center gap-3 self-end pb-2 text-sm"><Switch checked={!!pk.highlighted} onCheckedChange={(v) => p.setValue(updateAt(p.value, i, "highlighted", v))} data-testid={`pkg-${i}-highlight`} /> Highlight this package</label>
          </div>
        ))}
      </Panel>
    </div>
  );
};

export const FounderTab = () => {
  const f = useSection("founder");
  if (!f.value) return null;
  const set = (k) => (v) => f.setValue({ ...f.value, [k]: v });
  return (
    <Panel title="Founder" hint="Shown on the About page. Replace the [placeholders] with your own words." onSave={f.save} saving={f.saving} testid="admin-founder">
      <div className="grid gap-4 sm:grid-cols-2">
        <T label="Name" value={f.value.name} onChange={set("name")} testid="founder-name-input" />
        <T label="Title" value={f.value.title} onChange={set("title")} testid="founder-title-input" />
      </div>
      <ImageField label="Photograph" value={f.value.photo} onChange={set("photo")} testid="founder-photo-upload" />
      <T label="Short biography" rows={4} value={f.value.bio} onChange={set("bio")} testid="founder-bio-input" />
      <T label="Creative journey" rows={4} value={f.value.journey} onChange={set("journey")} testid="founder-journey-input" />
      <T label="Personal inspiration" rows={4} value={f.value.inspiration} onChange={set("inspiration")} testid="founder-inspiration-input" />
    </Panel>
  );
};

export const ContactTab = () => {
  const c = useSection("contact");
  const s = useSection("socials");
  if (!c.value || !s.value) return null;
  const save = async () => { await c.save(); await s.save(); };
  return (
    <Panel title="Contact & social links" hint="Leave a field empty to hide it from the website." onSave={save} saving={c.saving || s.saving} testid="admin-contact">
      <div className="grid gap-4 sm:grid-cols-2">
        <T label="Public email" value={c.value.email} onChange={(v) => c.setValue({ ...c.value, email: v })} testid="contact-email-input" />
        <T label="Phone" value={c.value.phone} onChange={(v) => c.setValue({ ...c.value, phone: v })} placeholder="+91 …" testid="contact-phone-input" />
        <T label="WhatsApp number" value={c.value.whatsapp} onChange={(v) => c.setValue({ ...c.value, whatsapp: v })} placeholder="+91 …" testid="contact-whatsapp-input" />
        <T label="Location" value={c.value.location} onChange={(v) => c.setValue({ ...c.value, location: v })} placeholder="City, Country" testid="contact-location-input" />
        <T label="Instagram URL" value={s.value.instagram} onChange={(v) => s.setValue({ ...s.value, instagram: v })} placeholder="https://instagram.com/…" testid="social-instagram-input" />
        <T label="Facebook URL" value={s.value.facebook} onChange={(v) => s.setValue({ ...s.value, facebook: v })} placeholder="https://facebook.com/…" testid="social-facebook-input" />
        <T label="YouTube URL" value={s.value.youtube} onChange={(v) => s.setValue({ ...s.value, youtube: v })} placeholder="https://youtube.com/@…" testid="social-youtube-input" />
      </div>
    </Panel>
  );
};
